import {
    getScrollableElementPosition,
    getSwipeDirection,
    isScrollableElement,
    preventElementScroll,
    ScrollPosition,
    SwipeAxis,
    SwipeDirection,
} from "../../utils";

export class ScrollLock {
    private abortController: AbortController | null = null;
    private touchStartClientY = 0;

    setActive(isActive: boolean): void {
        const isCurrentlyActive = this.abortController !== null;

        if (isActive === isCurrentlyActive) {
            return;
        }

        if (!isActive) {
            this.abortController?.abort();
            this.abortController = null;
            return;
        }

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        document.addEventListener("wheel", this.handleScrollAttempt, { passive: false, signal });
        document.addEventListener("touchmove", this.handleScrollAttempt, { passive: false, signal });
        document.addEventListener("touchstart", this.handleTouchStart, { passive: true, signal });
    }

    private readonly handleTouchStart = (event: TouchEvent): void => {
        this.touchStartClientY = event.touches[0]?.clientY ?? 0;
    };

    private readonly handleScrollAttempt = (event: WheelEvent | TouchEvent): void => {
        const target = event.target;

        if (!(target instanceof HTMLElement)) {
            return;
        }

        const popupElement = target.closest<HTMLElement>(".popup");

        if (popupElement !== null) {
            const direction = this.getScrollDirection(event);

            if (this.canScrollInsidePopup(target, popupElement, direction)) {
                return;
            }
        }

        preventElementScroll(event);
    };

    private getScrollDirection(event: WheelEvent | TouchEvent): SwipeDirection | null {
        if (event instanceof WheelEvent) {
            return event.deltaY < 0 ? SwipeDirection.Down : SwipeDirection.Up;
        }

        const currentClientY = event.touches[0]?.clientY ?? this.touchStartClientY;

        return getSwipeDirection(SwipeAxis.Y, this.touchStartClientY, currentClientY);
    }

    private canScrollInsidePopup(
        target: HTMLElement,
        popupElement: HTMLElement,
        direction: SwipeDirection | null,
    ): boolean {
        for (
            let element: HTMLElement | null = target;
            element !== null && element !== popupElement;
            element = element.parentElement
        ) {
            if (isSheetSelect(element) || !isScrollableElement(element)) {
                continue;
            }

            if (!isScrollAtBound(element, direction)) {
                return true;
            }
        }

        return false;
    }
}

function isSheetSelect(element: HTMLElement): boolean {
    return element.classList.contains("form-block_sheet-select");
}

function isScrollAtBound(element: HTMLElement, direction: SwipeDirection | null): boolean {
    const position = getScrollableElementPosition(element);

    const isAtBottomAndMovingUp = position === ScrollPosition.Bottom && direction === SwipeDirection.Up;
    const isAtTopAndMovingDown = position === ScrollPosition.Top && direction === SwipeDirection.Down;

    return isAtBottomAndMovingUp || isAtTopAndMovingDown;
}